import { useEffect, useMemo, useRef, useState } from 'react';
import { adaptStudyDeliveryToMathWorksheet } from '../../features/learningOrchestrator/studyDeliveryAdapter';
import { createTodayMathAssignmentV2, type AssignmentEngineRpcClient } from '../../features/learningPlanV2/assignmentAdapter';
import { getStudyResumeV2, startStudySessionV2, type LearningEngineV2RpcClient } from '../../features/learningEngineV2/runtimeAdapter';
import type { StudyDeliveryV2 } from '../../features/learningEngineV2/studyDeliveryContract';
import type { StudyEvaluationState } from '../../features/learningEngineV2/studyEvaluationFlow';
import { MathStudySessionV2Page, type StudyRuntimeBindingV2 } from '../../features/studyV2/MathStudySessionV2Page';
import { getSupabaseClient } from '../../lib/supabase';
import { env } from '../../lib/env';

function makeRpcClient(): AssignmentEngineRpcClient & LearningEngineV2RpcClient {
  const supabase = getSupabaseClient();
  if (!supabase) throw new Error('BelajarKu belum terhubung ke layanan belajar.');
  return {
    rpc: async (functionName, args) => {
      const { data, error } = await supabase.rpc(functionName, args);
      return {
        data,
        error: error ? { code: error.code, message: error.message, details: error.details, hint: error.hint } : null,
      };
    },
  };
}

function resolveDevLearningDate(): string | null {
  if (env.appEnv === 'production') return null;
  const raw = new URLSearchParams(window.location.search).get('date');
  if (!raw) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
  const parsed = new Date(`${raw}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== raw) return null;
  return raw;
}

export function CloudMathStudyV2Page() {
  const clientSessionId = useRef(`study-v2-${crypto.randomUUID()}`);
  const devLearningDate = useMemo(() => resolveDevLearningDate(), []);
  const [delivery, setDelivery] = useState<StudyDeliveryV2 | null>(null);
  const [runtime, setRuntime] = useState<StudyRuntimeBindingV2 | null>(null);
  const [initialEvaluation, setInitialEvaluation] = useState<StudyEvaluationState | undefined>(undefined);
  const [terminalMode, setTerminalMode] = useState<'completed' | 'await_review' | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const client = makeRpcClient();
        const nextDelivery = await createTodayMathAssignmentV2(client, { learningDate: devLearningDate });
        const resume = await getStudyResumeV2(client, nextDelivery.assignment.id);
        if (cancelled) return;
        setDelivery(nextDelivery);

        if (resume.mode === 'completed' || resume.mode === 'await_review') {
          setTerminalMode(resume.mode);
          return;
        }

        if (resume.mode === 'resume_study') {
          setRuntime({
            client,
            studySessionId: resume.learningSessionId!,
            assignmentId: nextDelivery.assignment.id,
            worksheetVersionId: nextDelivery.assignment.worksheetVersionId,
          });
          return;
        }

        if (resume.mode === 'correction') {
          setRuntime({
            client,
            studySessionId: resume.learningSessionId!,
            assignmentId: nextDelivery.assignment.id,
            worksheetVersionId: nextDelivery.assignment.worksheetVersionId,
          });
          setInitialEvaluation({
            mode: 'correction',
            clientBatchSubmissionId: resume.clientBatchSubmissionId!,
            correctionRound: resume.correctionRound,
            remainingQuestionInstanceIds: resume.remainingQuestionInstanceIds,
          });
          return;
        }

        const session = await startStudySessionV2(client, {
          clientSessionId: clientSessionId.current,
          assignmentId: nextDelivery.assignment.id,
        });
        if (cancelled) return;
        setRuntime({ client, studySessionId: session.sessionId, assignmentId: nextDelivery.assignment.id, worksheetVersionId: nextDelivery.assignment.worksheetVersionId });
      } catch (cause) {
        if (!cancelled) setError(cause instanceof Error ? cause.message : 'Latihan belum dapat dimulai.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [devLearningDate]);

  const worksheet = useMemo(() => delivery ? adaptStudyDeliveryToMathWorksheet(delivery) : null, [delivery]);
  if (loading) return <section className="card study-loading-card"><h1>Menyiapkan latihanmu…</h1><p className="muted">Sebentar, BelajarKu sedang menyiapkan worksheet hari ini.{devLearningDate ? ` Tanggal uji DEV: ${devLearningDate}.` : ''}</p></section>;
  if (error) return <section className="card"><h1>Latihan belum dapat dimulai</h1><p className="feedback feedback--error">{error}</p></section>;
  if (terminalMode === 'completed') return <section className="card completion-card"><h1>Bagus, latihan ini sudah selesai! 🎉</h1><p className="muted">Hasil dan proses belajarmu sudah tersimpan.{devLearningDate ? ` Tanggal uji DEV: ${devLearningDate}.` : ''}</p></section>;
  if (terminalMode === 'await_review') return <section className="card"><h1>Jawabanmu sudah dikirim</h1><p className="muted">Tutor akan melihat bagian yang memerlukan perhatian. Kamu bisa kembali ke Progress untuk melihat riwayatmu.</p></section>;
  if (!worksheet || !runtime) return <section className="card"><h1>Latihan belum siap</h1></section>;
  return <MathStudySessionV2Page worksheet={worksheet} runtime={runtime} initialEvaluation={initialEvaluation} />;
}
